import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueries, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { useDataApi } from "@core/apis";
import {
  getAverageSensorDataPerSubstationOptions,
  getSensorsDataPerSubstation,
  Sensor,
} from "@core/apis/shared";

import { useClimateSensorsQuery } from "../customQueryHooks";
import { AVERAGE_SENSOR_UID } from "../IndoorClimate/SensorOverview";
import { getFriendlySensorName } from "../sensorUtils";

export function useSensorsWithData(
  selectedSubstationId: string,
  dateRange: { startDate: DateTime; endDate: DateTime }
) {
  const dataApi = useDataApi();
  const { t } = useTranslation(["propai", "_common"]);
  // Fetch all sensors for the substation
  const { data: substationSensors, isLoading: fetchingSensors } =
    useClimateSensorsQuery(selectedSubstationId);

  // Fetch average sensor data
  const {
    data: averageSensorData,
    isSuccess: fetchedAverageSensorData,
    isLoading: fetchingAverageSensorData,
  } = useQuery(
    getAverageSensorDataPerSubstationOptions(
      dataApi,
      selectedSubstationId,
      "living_air_temperature",
      dateRange.startDate,
      dateRange.endDate
    )
  );

  // Fetch real sensor data for each sensor
  const {
    data: realSensorData,
    isSuccess: realSensorDataSuccess,
    isLoading: realSensorDataLoading,
  } = useQueries({
    queries: (substationSensors ?? [])
      // Only fetch data for living_air_temperature sensors (they contain both temp and humidity values)
      .filter((sensor) => sensor.sensor_type_technical_name === "living_air_temperature")
      .map((sensor) => {
        return {
          ...getSensorsDataPerSubstation(
            dataApi,
            selectedSubstationId,
            sensor.uid,
            dateRange.startDate,
            dateRange.endDate
          ),
          uid: sensor.uid,
        };
      }),
    combine: (results) => {
      const sensors = results.map((result) => {
        if (result.isSuccess) {
          return result.data;
        }
        return [];
      });

      const hasSuccessfulResults = results.some((result) => result.isSuccess);
      const allSettled = results.every((result) => !result.isPending);

      return {
        data: sensors,
        isSuccess: hasSuccessfulResults && allSettled,
        isLoading: results.some((result) => result.isPending),
      };
    },
  });

  // Optimized sensorsWithData composition with early returns
  const sensorsWithData: Sensor[] = useMemo(() => {
    const sensors: Sensor[] = [];

    // Early return if no data
    if (!substationSensors?.length) return sensors;

    // Process real sensor data
    if (realSensorDataSuccess && realSensorData?.length > 0) {
      // Pre-filter temperature sensors once
      const temperatureSensors = substationSensors.filter(
        (sensor) => sensor.sensor_type_technical_name === "living_air_temperature"
      );

      // Map sensor data with optimized label generation
      const mappedSensors = realSensorData
        .map((sensorData, index) => {
          const sensor = temperatureSensors[index];
          return {
            ...sensor,
            data: sensorData,
            label: getFriendlySensorName(sensor),
          };
        })
        .filter(Boolean); // Remove any undefined mappings

      sensors.push(...mappedSensors);
    }

    // Add average sensor if available
    if (averageSensorData && fetchedAverageSensorData) {
      sensors.push({
        uid: AVERAGE_SENSOR_UID,
        external_id: "Average",
        sensor_type_technical_name: "living_air_temperature",
        data: averageSensorData,
        street_address: "Average",
        apartment: "",
        label: `${t("_common:text_average")}`,
      });
    }

    return sensors;
  }, [
    t,
    averageSensorData,
    fetchedAverageSensorData,
    realSensorData,
    realSensorDataSuccess,
    substationSensors,
  ]);

  return {
    realSensorData,
    sensorsWithData,
    substationSensors,
    fetchingSensors,
    realSensorDataSuccess,
    realSensorDataLoading,
    averageSensorData,
    fetchedAverageSensorData,
    fetchingAverageSensorData,
  };
}
